import React, { useState, useEffect } from "react";
import userApiConfig from "../../config/userApiConfig";
import TextBox from "carbon-react/lib/__experimental__/components/textbox";
import SearchButton from "../common/SearchButton";
import { userFieldNames } from "../../constants/model";
import {
  Select,
  Option,
} from "carbon-react/lib/__experimental__/components/select";
import { Row, Column } from "carbon-react/lib/components/row";
import { isAValidEmailAddress } from "../../constants/validation";
import { useAppContext } from "../../contexts/AppContext";

import "./UserFilters.css";

const UserFilters = (props) => {
  const { agentEntitlement } = useAppContext();
  const [searchTermValid, setSearchTermValid] = useState(true);

  const getSearchTermMessage = (searchBy) => {
    const sanitizedSearchBy =
      searchBy && searchBy.length > 0 ? searchBy.trim() : "";

    const optionMetadata = filterByOptionMetadata.find(
      (metadata) => metadata.value === sanitizedSearchBy
    );
    return optionMetadata?.validationMsg;
  };

  const userNameMatchType = agentEntitlement?.users
    ?.canPerformWildcardNameSearch
    ? "partial"
    : "exact";

  const filterByOptionMetadata = [
    {
      text: "Email",
      value: userFieldNames.email,
      key: userFieldNames.email,
      validationMsg: "Enter value for exact match (full email address)",
    },
    {
      text: "Name",
      value: userFieldNames.name,
      key: userFieldNames.name,

      validationMsg: `Enter value for ${userNameMatchType} match (${userApiConfig.minSearchTermLength} or more chars)`,
    },
    {
      text: "Auth0Id",
      value: userFieldNames.auth0Id,
      key: userFieldNames.auth0Id,
      validationMsg: `Enter value for exact match (${userApiConfig.minSearchTermLength} or more chars)`,
    },
  ];

  const isAValidFunc = (func) => {
    return func && typeof func === "function";
  };

  const searchingByField = (fieldName) => {
    return (
      props?.userFilters?.searchBy && props.userFilters.searchBy === fieldName
    );
  };

  const [searchTermMsg, setSearchTermMsg] = useState(
    getSearchTermMessage(props.userFilters.searchBy)
  );

  const onSearchByChanged = (newSearchBy) => {
    setSearchTermValid(true);
    setSearchTermMsg(getSearchTermMessage(newSearchBy));
    updateFilterState(() => {
      props.userFilters.searchBy = newSearchBy;
    });
  };

  const onSearchValueChanged = (newSearchValue) => {
    setSearchTermValid(true);
    updateFilterState(() => {
      props.userFilters.searchValue = newSearchValue;
    });
  };

  const updateFilterState = (mutateState) => {
    if (isAValidFunc(mutateState)) mutateState();
    props.onUpdateUserFilters({ ...props.userFilters });
  };

  const validateSearchTerm = () => {
    let sanitizedSearchTerm = props.userFilters?.searchValue || "";
    if (
      searchingByField(userFieldNames.email) ||
      searchingByField(userFieldNames.auth0Id)
    ) {
      sanitizedSearchTerm = sanitizedSearchTerm.trim();
      updateFilterState(() => {
        props.userFilters.searchValue = sanitizedSearchTerm;
      });
    }

    let valid = sanitizedSearchTerm.length >= userApiConfig.minSearchTermLength;

    if (valid && searchingByField(userFieldNames.email)) {
      valid = isAValidEmailAddress(props.userFilters?.searchValue);
    }

    setSearchTermValid(valid);
    return valid;
  };

  const onSubmit = () => {
    if (!validateSearchTerm()) return;
    props.onSearch();
  };

  const searchValueValidationAttributes = {
    hasWarning: true,
    inputIcon: "warning",
    tooltipMessage: searchTermMsg,
  };

  const searchBySelection = (
    <Select
      id="user-search-filter-search-by"
      autoFocus
      preventFocusAutoOpen={true}
      filterable={false}
      value={props.userFilters.searchBy}
      onChange={(e) => {
        onSearchByChanged(e.target.value[0].optionValue);
      }}
    >
      {filterByOptionMetadata.map((filterOption) => {
        return (
          <Option
            id={`user-search-filter-search-by-${filterOption.value}`}
            key={filterOption.key}
            text={filterOption.text}
            value={filterOption.value}
          />
        );
      })}
    </Select>
  );

  const searchValueInput = (
    <TextBox
      id="user-search-filter-search-term"
      placeholder={`${searchTermMsg}...`}
      value={props.userFilters.searchValue}
      onBlur={validateSearchTerm}
      onChange={(e) => {
        onSearchValueChanged(e.target.value);
      }}
      {...(!searchTermValid ? searchValueValidationAttributes : {})}
    />
  );

  return (
    <form
      className="userFilters"
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit();
      }}
    >
      <Row className="user-search-filter-row" columns="9" gutter="medium">
        <Column
          className="user-search-filter-column"
          columnAlign="left"
          columnSpan="2"
        >
          {searchBySelection}
        </Column>
        <Column
          className="user-search-filter-column"
          columnAlign="left"
          columnSpan="3"
        >
          {searchValueInput}
        </Column>
        <Column
          className="user-search-filter-column"
          columnAlign="left"
          columnSpan="2"
        >
          <SearchButton id="user-search-filter-search-btn" type="submit" />
        </Column>
      </Row>
    </form>
  );
};

export default UserFilters;
